/**
 * Durable offline cache for Design Calendar (IndexedDB).
 * One record per year: snapshot + pendingOps[].
 */
import {
  applyPendingOpsToSnapshot,
  buildYearSnapshot,
} from "./designCalendarOfflinePure";

const DB_NAME = "design-calendar-offline-v1";
const DB_VERSION = 1;
const STORE_YEARS = "years";

let dbPromise = null;

function openDb() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB no disponible"));
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error("IDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_YEARS)) {
        db.createObjectStore(STORE_YEARS, { keyPath: "anio" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
  return dbPromise;
}

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IDB request failed"));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("IDB tx failed"));
    tx.onabort = () => reject(tx.error || new Error("IDB tx aborted"));
  });
}

export async function getYearRecord(anio) {
  const year = Number(anio);
  if (!Number.isInteger(year) || year < 2000) return null;
  try {
    const db = await openDb();
    return (
      (await idbReq(
        db.transaction(STORE_YEARS).objectStore(STORE_YEARS).get(year),
      )) || null
    );
  } catch {
    return null;
  }
}

export async function hasPendingOps(anio) {
  const row = await getYearRecord(anio);
  return Boolean(row?.pendingOps?.length);
}

export async function getPendingOps(anio) {
  const row = await getYearRecord(anio);
  return Array.isArray(row?.pendingOps) ? row.pendingOps : [];
}

export async function getMergedYearSnapshot(anio) {
  const row = await getYearRecord(anio);
  if (!row?.snapshot) return null;
  return applyPendingOpsToSnapshot(row.snapshot, row.pendingOps || []);
}

export async function putYearSnapshot(anio, data, { keepPending = true } = {}) {
  const year = Number(anio);
  if (!Number.isInteger(year) || year < 2000) {
    throw new Error("Año inválido");
  }
  const db = await openDb();
  const prev = await idbReq(
    db.transaction(STORE_YEARS).objectStore(STORE_YEARS).get(year),
  );
  const snapshot = buildYearSnapshot({ ...data, anio: year });
  const record = {
    anio: year,
    offlineEnabled: true,
    snapshot,
    pendingOps:
      keepPending && Array.isArray(prev?.pendingOps) ? prev.pendingOps : [],
    cachedAt: new Date().toISOString(),
  };
  const tx = db.transaction(STORE_YEARS, "readwrite");
  tx.objectStore(STORE_YEARS).put(record);
  await txDone(tx);
  return record;
}

export async function enqueueYearOp(anio, op) {
  const year = Number(anio);
  const db = await openDb();
  const tx = db.transaction(STORE_YEARS, "readwrite");
  const store = tx.objectStore(STORE_YEARS);
  const prev = (await idbReq(store.get(year))) || {
    anio: year,
    offlineEnabled: true,
    snapshot: null,
    pendingOps: [],
  };
  const pendingOps = [
    ...(prev.pendingOps || []),
    { ...op, queuedAt: new Date().toISOString() },
  ];
  store.put({
    ...prev,
    offlineEnabled: true,
    pendingOps,
    cachedAt: new Date().toISOString(),
  });
  await txDone(tx);
  return pendingOps;
}

export async function setPendingOps(anio, pendingOps) {
  const year = Number(anio);
  const row = await getYearRecord(year);
  if (!row) return;
  const db = await openDb();
  const tx = db.transaction(STORE_YEARS, "readwrite");
  tx.objectStore(STORE_YEARS).put({
    ...row,
    pendingOps: Array.isArray(pendingOps) ? pendingOps : [],
    cachedAt: new Date().toISOString(),
  });
  await txDone(tx);
}

export async function clearPendingOps(anio) {
  await setPendingOps(anio, []);
}
